import { FC, useEffect, useState } from 'react';
import { Avatar, Badge, Col, Row } from 'antd';
import { TbCopyPlus, TbLayout } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { LearnMoreFooter } from '@/shared';
import { SetStateMethod } from '@/types';
import { centerComponentInMiddleofView } from '@/utilities';

import DashboardTemplateItem from './components/DashboardTemplateItem';

interface DashboardTemplatesProps {
    template: string;
    setTemplate: SetStateMethod<string>;
}

const DashboardTemplates: FC<DashboardTemplatesProps> = ({ template, setTemplate }): JSX.Element => {
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(false);
    }, []);

    const { token } = useAntDesignTheme();

    const dashboardTemplates = [
        {
            key: 'empty',
            label: <>Empty Dashboard</>,
            avatar: (
                <Avatar
                    size='small'
                    icon={<TbLayout style={{ color: token.colorTextTertiary }} />}
                    shape='square'
                    style={{ border: `1px solid ${token.colorBorder}` }}
                />
            ),
            description: (
                <>
                    Start with an empty dashboard. Ideal for custom workflows, bespoke analyses, or teams with an
                    established internal research process.
                </>
            ),
        },
        {
            key: 'portfolioMonitoring',
            label: <>Portfolio Monitoring</>,
            avatar: (
                <Avatar
                    size='small'
                    icon={<TbLayout style={{ color: token.gold5 }} />}
                    shape='square'
                    style={{ border: `1px solid ${token.gold2}`, backgroundColor: token.gold1 }}
                />
            ),
            description: (
                <>
                    Monitor active holdings with key performance indicators, exposure metrics, and ongoing company
                    updates. Designed for continuous oversight of portfolio companies.
                </>
            ),
        },
        {
            key: 'dueDiligencePipeline',
            label: <>Due Diligence Pipeline</>,
            avatar: (
                <Avatar
                    size='small'
                    icon={<TbLayout style={{ color: token.gold5 }} />}
                    shape='square'
                    style={{ border: `1px solid ${token.gold2}`, backgroundColor: token.gold1 }}
                />
            ),
            description: (
                <>
                    A structured starting point for investment due diligence, including company overview, qualitative
                    assessment, and early financial analysis. Designed for evaluating new opportunities and tracking
                    diligence progress.
                </>
            ),
        },
        {
            key: 'researchBreakdown',
            label: <>Research Breakdown</>,
            avatar: (
                <Avatar
                    size='small'
                    icon={<TbLayout style={{ color: token.gold5 }} />}
                    shape='square'
                    style={{ border: `1px solid ${token.gold2}`, backgroundColor: token.gold1 }}
                />
            ),
            description: (
                <>
                    Organize fundamental research by category, theme, or hypothesis. Useful for deep dives, investment
                    memos, and maintaining a structured view of qualitative and quantitative research inputs.
                </>
            ),
        },
        {
            key: 'investmentCommitteeOverview',
            label: <>Investment Committee Overview</>,
            avatar: (
                <Avatar
                    size='small'
                    icon={<TbLayout style={{ color: token.gold6 }} />}
                    shape='square'
                    style={{ border: `1px solid ${token.gold2}`, backgroundColor: token.gold1 }}
                />
            ),
            description: (
                <>
                    Structure an investment decision for formal review. Includes thesis, risks, financial analysis,
                    scenarios, and supporting materials for IC discussion and approval.
                </>
            ),
        },
        {
            key: 'opportunityFunnel',
            label: <>Opportunity Funnel</>,
            avatar: (
                <Avatar
                    size='small'
                    icon={<TbLayout style={{ color: token.gold6 }} />}
                    shape='square'
                    style={{ border: `1px solid ${token.gold2}`, backgroundColor: token.gold1 }}
                />
            ),
            description: (
                <>
                    Track inbound opportunities from initial screen to diligence. Designed to manage deal flow, surface
                    bottlenecks, and retain institutional memory on passed opportunities.
                </>
            ),
        },
    ];

    return (
        <div
            style={{
                margin: '1rem auto',
                width: 'auto',
                ...centerComponentInMiddleofView,
            }}
        >
            <span
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    gap: '0.75rem',
                    textAlign: 'center',
                }}
            >
                <div>
                    <Badge
                        offset={[-2, 38]}
                        count={
                            <Avatar
                                size={'small'}
                                shape='square'
                                style={{
                                    marginBottom: '1.5rem',
                                    border: `2px solid ${token.colorBgContainer}`,
                                    backgroundColor: token.gold1,
                                }}
                                icon={
                                    <TbCopyPlus
                                        style={{
                                            color: token.gold5,
                                        }}
                                        size='75%'
                                    />
                                }
                            />
                        }
                    >
                        <Avatar
                            size={16 * 2.5}
                            icon={<TbLayout size={16 * 1.5} style={{ color: token.colorTextTertiary }} />}
                            shape='square'
                            style={{ border: `1px solid ${token.colorBorder}` }}
                        />
                    </Badge>
                </div>
                <div style={{ color: token.colorTextSecondary, marginTop: '0.25rem' }}>Pick Dashboard Template</div>
                <div
                    style={{
                        textAlign: 'center',
                        width: '25rem',
                    }}
                >
                    <small style={{ color: token.colorTextTertiary }}>
                        Select a Template to start building insight-rich analytical views around your investment
                        research & due diligence workflows.
                    </small>
                </div>
            </span>
            <Row gutter={[16, 16]} wrap={true} style={{ margin: '1rem 0' }}>
                {dashboardTemplates?.map((dt, dtIndex) => {
                    return (
                        <Col span={12} key={dt?.key}>
                            <DashboardTemplateItem
                                dt={dt}
                                dtIndex={dtIndex}
                                template={template}
                                setTemplate={setTemplate}
                            />
                        </Col>
                    );
                })}
            </Row>
            <LearnMoreFooter topic='Dashboards' />
            {/* <Button onClick={handleCreateReport} style={{ padding: '0 0.5rem', marginTop: '1rem' }}>
                <TbPlus size='1rem' style={{ verticalAlign: '-0.2rem' }} />
                Create Report
            </Button> */}
        </div>
    );
};

export default DashboardTemplates;
